import { FC, useState } from 'react';
import { useTranslate } from 'hooks/useTranslate';
import { MultiValue, SingleValue } from 'react-select';

import { SelectorOption } from 'types';
import { formatNumber } from 'utils/formats';

import CustomAsyncSelect from 'modules/_shared/components/CustomAsyncSelect';

import { useReclassificationModal } from '../context/ReclassificationModalContext';
import { CustomRange } from '../types/CustomRange';

type RangeRowProps = {
  index: number;
  range: CustomRange;
};

const RangeRow: FC<RangeRowProps> = ({ index, range }: RangeRowProps) => {
  const { t } = useTranslate();
  const { data, actions } = useReclassificationModal();

  const [selectedClassOption, setSelectedClassOption] = useState<
    SelectorOption | undefined
  >(range.selectedShareClassOption);

  const rowKey = `${range.partner}-${range.shareClass}-${range.from}-${range.to}`;
  const classKey = `entire-class-${range.shareClass}`;
  const from = range?.newFrom || range.from;
  const to = range?.newTo || range.to;
  const isLoading = data.loadingKeys.includes(rowKey) && data.isLoadingRanges;
  const isDisabledRow = data.disabledKeys.includes(rowKey);
  const isDisabledClass = data.disabledKeys.includes(classKey);

  const handleChangeHoldingClass = (
    newValue: MultiValue<SelectorOption> | SingleValue<SelectorOption>
  ) => {
    setSelectedClassOption(newValue as SelectorOption);
    actions.handleEditRange(rowKey, {
      ...range,
      key: rowKey,
      selectedShareClassOption: newValue as SelectorOption,
    });
  };

  return (
    <div className="row g-1">
      <div className="col-4">
        <div className="input-group">
          <span className="input-group-text" id={`share-from-${index}`}>
            {t('OfTheNo')}
          </span>
          {isLoading ? (
            <div className="form-control placeholder-glow h-38px">
              <span className="placeholder col-12" />
            </div>
          ) : (
            <input
              type="text"
              className="form-control form-control-outlined h-38px p-0 text-center"
              value={formatNumber(from)}
              disabled
              readOnly
            />
          )}
        </div>
      </div>

      <div className="col-4">
        <div className="input-group">
          <span className="input-group-text" id={`share-to-${index}`}>
            {t('ToTheNo')}
          </span>
          {isLoading ? (
            <div className="form-control placeholder-glow h-38px">
              <span className="placeholder col-12" />
            </div>
          ) : (
            <input
              type="text"
              className="form-control form-control-outlined h-38px p-0 text-center"
              value={formatNumber(to)}
              disabled
              readOnly
            />
          )}
        </div>
      </div>

      <div className="col-4">
        <CustomAsyncSelect
          id={`class-select-input-${index}`}
          value={selectedClassOption}
          onChange={handleChangeHoldingClass}
          onFetchData={actions.handleFetchHoldingClassByTerm}
          placeholder={t('SearchClass')}
          minimumInputLength={1}
          noDataLabelKey="NoClassesHaveBeenFound"
          loadingLabelKey="LoadingClasses"
          errorLabelKey="ErrorLoadingClasses"
          defaultOptions={data.holdingClassOptions}
          isClearable={false}
          isDisabled={isDisabledRow || isLoading || isDisabledClass}
        />
      </div>
    </div>
  );
};

export default RangeRow;
